import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import { Container, Row, Col, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Slider } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import QuoteCard from '../Common/Cards/QuoteCard';
import CoverageLengths from '../../Helpers/Lists/CoverageLengths/DigitalCoverageLengths';
import HealthClasses from '../../Helpers/Lists/HealthClasses';
import ErrorHandler from '../../Helpers/Errors/ErrorHandler';
import NoResultsFoundError from '../../Helpers/Errors/CustomErrors/NoResultsFoundError';
import { SelectInput, TextInput } from '../../Helpers/Inputs';
import { CollapsibleBasicInfoSummary } from '../Common/SubComponents/BasicInfoSummary';
import NumberFormatDollars from '../../Helpers/Formatters/NumberFormatDollars';
import ShowOtherOptions from '../Common/SubComponents/ShowOtherOptions';
import NeedsAnalysisButton from '../../Tools/NeedsAnalysis/NeedsAnalysisButton';
import NoQuoteAvailable from '../Common/SubComponents/NoQuoteAvailable';
import DigitalCoverageAmounts from '../../Helpers/Lists/CoverageAmounts/DigitalCoverageAmounts.js';
import { HeaderStepper } from '../Common/HeaderStepper/HeaderStepper';
import { getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY, getBasicInfo, setBasicInfo, SESSION_STORAGE_CLIENTINFO_KEY } from '../../../sessionStorage';
import GetProductTypeEnum from '../../../types/productType';
import { saveSessionEvent } from '../../../session';
import ProductDetails from '../FullUW/Helpers/ProductDetails';
import QuoteFor from '../Common/QuoteFor/QuoteFor';
import LGAApplyDialog from './LGA/LGAApplyDialog';
import Age from '../../Helpers/Dates/Age';

import '../Quoter.scss';
import { QuoteProductID } from '../Common/QuoteProductID';

export default function FullDigital(props) {
  useEffect(() => {
    saveSessionEvent();
  }, [props.location]);

  const basicInfo = getBasicInfo();

  if (!basicInfo.birthdate) {
    window.location.replace('/landing/digital-term');
  }

  const [loading, setLoading] = useState(true);

  const coverageLengths = CoverageLengths();
  const healthClasses = HealthClasses();

  const [termLength, setTermLength] = useState(basicInfo.coverageLength || 20);
  const [coverageAmount, setCoverageAmount] = useState(basicInfo.faceAmount || 500000);
  const [healthClass, setHealthClass] = useState(basicInfo.healthClass || 'PP');
  const [quoteResults, setQuoteResults] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [applyDialog, setApplyDialog] = useState(null);
  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);

  var accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const termAccountProduct = accountObj && accountObj.accountProducts.find(e => e.productTypeId === GetProductTypeEnum().FullUW);
  const bestowHandoffAccount = termAccountProduct && termAccountProduct.bestowHandoffAccount;

  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;

  const isHavenSI = accountObj.accountProducts.some(function (item) {
    return item.productTypeId === GetProductTypeEnum().HavenSI;
  });

  const quoteFields = {
    birthdate: basicInfo.birthdate,
    stateOfResidence: basicInfo.state,
    gender: basicInfo.gender,
    tobacco: basicInfo.tobacco,
    coverageAmount: coverageAmount,
    termLength: termLength,
    healthClass: healthClass,
    height: basicInfo.height,
    weight: basicInfo.weight,
    email: basicInfo.email,
    phone: basicInfo.phone,
  };

  // Open the modal when a quote is selected
  useEffect(() => {
    if (!selectedQuote || !selectedQuote.carrierName) {
      return;
    }

    switch (selectedQuote.carrierName) {
      case 'LGA':
        setApplyDialog(<LGAApplyDialog
          quoteFields={quoteFields}
          logQuote={LogQuote}
          quoteResult={selectedQuote}
          basicInfo={basicInfo}
        />);

        break;
    }

    setIsApplyDialogOpen(true);

  }, [selectedQuote]);
  
  const handleChange = (event, newValue) => {
    setCoverageAmount(newValue);
  };

  const LogQuote = (email, phone, quote, oneClickTypeId, referralId = null) => {

    //save quote log for bms

    return new Promise(resolve => {

      var requestString = JSON.stringify({
        CoverageAmount: coverageAmount,
        CoverageLength: termLength,
        State: basicInfo.state,
        BirthDate: format(new Date(basicInfo.birthdate), 'MM-dd-yyyy'),
        HealthClass: healthClass,
        Gender: basicInfo.gender,
        Tobacco: basicInfo.tobacco,
        Email: email,
        Phone: phone,
      });

      let jsonResponse;

      // set custom quote id in response if present 
      if (referralId) {
        var response = { ...quote };
        response.PartnerQuoteId = referralId;

        jsonResponse = JSON.stringify(response);
      }
      else {
        jsonResponse = JSON.stringify(quote);
      }

      fetch(`/api/QuoteLog/PostQuoteLog`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
        },
        body: JSON.stringify({
          OneClickTypeID: oneClickTypeId, //digital oneclick type
          Request: requestString,
          Response: jsonResponse,
          ApplicationID: accountObj.sessionId,
          OneClickAccountID: accountObj.account.oneClickAccountId,
          SessionID: accountObj.sessionId,
          PartnerQuoteId: referralId,
          ConsumerAccountId: accountObj.account.accountId
        }),
      }).then(() => resolve());
    });
  };

  const runQuote = (getIsSubscribed) => {
    const formattedDate = format(new Date(basicInfo.birthdate), 'MM-dd-yyyy');
    const feet = basicInfo.height.split('ft')[0].trim();
    const inches = basicInfo.height.split('ft')[1].split('in')[0].trim();
    const weight = basicInfo.weight.split(' ')[0];
    basicInfo.faceAmount = coverageAmount;
    basicInfo.termLength = termLength;

    let request = new URLSearchParams({
      coverageAmount: coverageAmount,
      termLength: termLength,
      stateCode: basicInfo.state,
      birthdate: formattedDate,
      healthClass: healthClass,
      gender: basicInfo.gender,
      tobacco: basicInfo.tobacco,
      heightFeet: feet,
      heightInches: inches,
      weight: weight,
      bestowShortUrlId: bestowHandoffAccount,
      OneClickAccountID: accountObj.account.oneClickAccountId,
      productIdentifier: 'HavenTerm',
      sessionId: accountObj.sessionId,
    })

    fetch(`${covrServicesUrl}/api/Digital/GetDigitalQuotes?${request.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (getIsSubscribed() === false) {
          return;
        }

        if (!data) {
          throw new NoResultsFoundError();
        }

        if (data.success !== false) {
          setQuoteResults(data);
        }

        setLoading(false);
      })
      .catch((error) => {
        if (getIsSubscribed() === false) {
          return;
        }
        ErrorHandler(error, setQuoteResults);
        setLoading(false);
      });
  };

  let debounce = null;
  useEffect(() => {
    setLoading(true);

    if (!termLength || !healthClass || !coverageAmount) {
      return;
    }

    const age = Age(basicInfo.birthdate)
    if (age < 18 || age >= 65) {
      props.history.push(`/quote/term-life-insurance/multi-carrier-term/pre-quote`);
    }

    let isSubscribed = true;

    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);

    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    };
  }, [termLength, healthClass, coverageAmount]);

  useEffect(() => {
    var coverageValue = getSessionStorageItem('INSURANCE_NEEDS_ANALYSIS');
    if (coverageValue && coverageValue > 0 && coverageValue < 3000000) {
      setCoverageAmount(parseInt(coverageValue, 10));
    }
  }, []);

  const onBack = () => {
    props.history.push(`/landing/term/digital-term`);
    window.scroll(0, 0);
  };

  const onDialogClosed = () => {
    setIsApplyDialogOpen(false);
    setSelectedQuote(null);
  }

  useEffect(() => {
    // dynamic term length based on age

    const age = Age(basicInfo.birthdate);
    if (age <= 39 && basicInfo.gender === "M" && basicInfo.tobacco === "false") {
      setTermLength(30);
    } else if (age <= 40 && basicInfo.gender === "F" && basicInfo.tobacco === "false") {
      setTermLength(30);
    } else if (age <= 39 && basicInfo.gender === "M" && basicInfo.tobacco === "true") {
      setTermLength(20);
    } else if (age <= 40 && basicInfo.gender === "F" && basicInfo.tobacco === "true") {
      setTermLength(20);
    } else if (age > 39 && age <= 50 && basicInfo.gender === "M") {
      setTermLength(20);
    } else if (age > 40 && age <= 50 && basicInfo.gender === "F") {
      setTermLength(20);
    } else if (age > 50) {
      setTermLength(10);
    }
  }, [])

  const handleQuoteSelection = (callbacks) => {
    const { onNext } = callbacks;
    onNext();
  }

  const quoteOnClick = (quote) => {
    if (quote.productID === QuoteProductID.HAVEN_TERM_PRODUCT_ID || quote.productID === QuoteProductID.BESTOW_PRODUCT_ID || quote.productID === QuoteProductID.LGA_PRODUCT_ID || quote.productID === QuoteProductID.HAVEN_SI_PRODUCT_ID) {
      var form = { ...props.fullFormState };
      form.pages.quoter.coverageAmount = coverageAmount;
      form.pages.quoter.term = termLength;
      form.pages.quoter.carrierName = quote.carrierName;
      form.pages.quoter.carrierLogoUrl = quote.logoUrl;
      form.pages.quoter.premium = quote.monthlyRate;
      form.pages.quoter.product = quote.product;
      form.pages.quoter.selectedQuote = quote;
      form.pages.quoter.healthClass = healthClass;
      form.pages.quoter.selectedQuote.quoteLogId = quoteResults.quoteLogId;
      props.onMultiFieldChange(form);

      var basicInfo = getBasicInfo();
      basicInfo.faceAmount = coverageAmount;
      basicInfo.termLength = termLength;
      setBasicInfo(basicInfo);

      const callbacks = {
        onNext: props.onNext,
      };

      handleQuoteSelection(callbacks);
    } else {
      setSelectedQuote(quote);
    }
  };

  const filteredData = quoteResults && quoteResults.length > 0 && quoteResults.filter(data => data.productID !== QuoteProductID.HAVEN_TERM_PRODUCT_ID && data.productID !== QuoteProductID.BESTOW_PRODUCT_ID && data.productID !== QuoteProductID.LGA_PRODUCT_ID && data.productID !== QuoteProductID.HAVEN_SI_PRODUCT_ID);

  return (
    <div>
      <Helmet>
        <title>Term life insurance - Get a Quote </title>
      </Helmet>
      <HeaderStepper hideProgressTracker step={1} totalSteps={1} stepName="Digital Quoter">
        <Container>

          <Row className="quoter haven-digital">
            <Col lg={4} className="mb-3">
              <Form>
                <h2>Get your quote</h2>
                <TextInput
                  name="coverageAmount"
                  id="coverageAmount"
                  labelAbove
                  label="Select Coverage"
                  value={coverageAmount}
                  onChange={(e) => setCoverageAmount(parseInt(e.target.value, 10))}
                  variant="outlined"
                  InputProps={{
                    inputComponent: NumberFormatDollars,
                    className: 'coverage-input',
                  }}
                />
                <Form.Group className="ml-3 mr-4">
                  <Slider
                    min={100000}
                    max={isHavenSI ? 500000 : 3000000}
                    step={null}
                    value={coverageAmount}
                    onChange={handleChange}
                    className="m-2"
                    marks={DigitalCoverageAmounts(isHavenSI)}
                  />
                </Form.Group>
                <Form.Group>
                  <SelectInput
                    name="CoverageLength"
                    id="CoverageLength"
                    value={termLength}
                    hideEmptyOption
                    onChange={(e) => setTermLength(e.target.value)}
                    label="Years of Coverage"
                    selectOptions={coverageLengths}
                  />
                </Form.Group>

                <Form.Group>
                  <SelectInput
                    name="HealthClass"
                    id="HealthClass"
                    hideEmptyOption
                    value={healthClass}
                    onChange={(e) => setHealthClass(e.target.value)}
                    label="Health Class"
                    selectOptions={healthClasses}
                  />
                </Form.Group>

                <CollapsibleBasicInfoSummary
                  gender={basicInfo.gender}
                  state={basicInfo.state}
                  birthdate={basicInfo.birthdate}
                  tobacco={basicInfo.tobacco}
                  height={basicInfo.height}
                  weight={basicInfo.weight}
                  showEditLink={true}
                  editLinkOnClick={onBack}
                />
              </Form>

              <NeedsAnalysisButton type="quote-screen" title='MY COVERAGE NEED' minCoverageAmount={100000} maxCoverageAmount={3000000} setCoverageAmount={setCoverageAmount} />

              <ShowOtherOptions healthClass={healthClass} terms={termLength} coverageAmount={coverageAmount} />
            </Col>

            <Col lg={8}>
              <QuoteResults
                coverageAmount={coverageAmount}
                termLength={termLength}
                results={quoteResults.quoteResults}
                tobacco={basicInfo.tobacco}
                loading={loading}
                setSelectedQuote={quoteOnClick}
              />
            </Col>
          </Row>
          
          {filteredData &&
            <Dialog open={isApplyDialogOpen} onClose={onDialogClosed} maxWidth={'xl'} fullWidth={true} scroll="body">
              <DialogContent className="apply-dialog-button">
                <IconButton className="apply-dialog-close-button" aria-label="close" onClick={onDialogClosed}>
                  <CloseIcon />
                </IconButton>
                {applyDialog}
              </DialogContent>
            </Dialog>
          }
        </Container>
      </HeaderStepper>
    </div>
  );
}

function QuoteResults(props) {

  if (props.loading) {
    return (
      <div className="loading-container">
        <CircularProgress />
      </div>
    );
  }

  const filteredResults = props.results && props.results.filter(r => r.monthlyRate > 0);

  if (filteredResults.length < 1) {
    if (props.coverageAmount < 50000) {
      return <NoQuoteAvailable subText="Some values may not meet minimum requirements. Please update the coverage amount or term length, or click 'Show other options' below." />;
    } else {
      return <NoQuoteAvailable subText="Some limits may have been exceeded. Please select a shorter term duration or click 'Show other options' below." />;
    }
  }

  return (
    <div className="quote-result-section">
      <QuoteFor coverageAmount={props.coverageAmount} separator productType={`${props.termLength} Year Term`} />

      {filteredResults && filteredResults.sort((a, b) => a.monthlyRate > b.monthlyRate).map((r, key) =>
        <QuoteResult
          key={key}
          coverageAmount={props.coverageAmount}
          termLength={props.termLength}
          tobacco={props.tobacco}
          result={r}
          loading={props.loading}
          onSelectClicked={() => props.setSelectedQuote(r)}
        />
      )}
    </div>
  )
}

QuoteResults.propTypes = {
  results: PropTypes.array,
  setSelectedQuote: PropTypes.func,
  coverageAmount: PropTypes.number,
  termLength: PropTypes.number,
  loading: PropTypes.bool,
}

function QuoteResult(props) {
  const [expanded, setExpanded] = useState(false);

  const { result, onSelectClicked } = props;

  return (
    <div className="quote-result-section">
      {result &&
        <QuoteCard
          carrierName={result.carrierName}
          productName={result.product}
          underwritingTime={result.underwritingTimeTypical}
          quote={result}
          expanded={expanded}
          expand={() => setExpanded(!expanded)}
          monthlyRate={result.monthlyRate}
          logoUrl={result.logoUrl}
          rating={result.rating}
          onClick={onSelectClicked}
          productDetails={<ProductDetails
            carrierName={result.carrierName}
            productDescription={result.productDescription}
            productInformation={result.productInformation}
            hasPermConversion={result.hasPermConversionOption}
          />}
        />
      }
    </div>
  );
}

QuoteResult.propTypes = {
  coverageAmount: PropTypes.number,
  termLength: PropTypes.number,
  result: PropTypes.object,
  loading: PropTypes.bool,
  setProductInfoDialogOpen: PropTypes.func,
}

FullDigital.propTypes = {};

FullDigital.DefaultFormState = () => {
  return {
    coverageAmount: 0,
    healthClass: 'P',
    coverageLength: 0,
  };
}



// WEBPACK FOOTER //
// ./src/components/Quoters/Digital/FullDigital.js